import { AxiosError } from 'axios';
import i18n from '@/i18n';

/**
 * Error categories for API errors
 */
export enum ErrorCategory {
  /** Network connectivity issues */
  NETWORK = 'NETWORK',
  /** Authentication/authorization failures */
  AUTH = 'AUTH',
  /** Validation errors (400, 422) */
  VALIDATION = 'VALIDATION',
  /** Server errors (500, 502, 503) */
  SERVER = 'SERVER',
  /** Unknown or unhandled errors */
  UNKNOWN = 'UNKNOWN',
}

/**
 * Custom API error class with categorization
 *
 * Extends Error to provide structured error information
 * for better error handling and user messaging.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public category: ErrorCategory,
    public statusCode?: number,
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Categorizes an error based on its type and status code
 *
 * @param error - The error to categorize (unknown type)
 * @returns An ApiError with appropriate category
 *
 * @example
 * ```ts
 * try {
 *   await apiCall();
 * } catch (error) {
 *   const apiError = categorizeError(error);
 *   console.log(apiError.category); // 'NETWORK', 'AUTH', etc.
 * }
 * ```
 */
export function categorizeError(error: unknown): ApiError {
  // Handle AxiosError
  if (error instanceof AxiosError) {
    const statusCode = error.response?.status;

    // Network errors (no response)
    if (!error.response) {
      return new ApiError(i18n.t('errors.network'), ErrorCategory.NETWORK, undefined, error);
    }

    // Authentication errors (401, 403)
    if (statusCode === 401 || statusCode === 403) {
      return new ApiError(
        statusCode === 401 ? i18n.t('errors.loginRequired') : i18n.t('errors.forbidden'),
        ErrorCategory.AUTH,
        statusCode,
        error
      );
    }

    // Validation errors (400, 422)
    if (statusCode === 400 || statusCode === 422) {
      const message = error.response?.data?.message || i18n.t('errors.invalidRequest');
      return new ApiError(message, ErrorCategory.VALIDATION, statusCode, error);
    }

    // Server errors (500, 502, 503, 504)
    if (statusCode && statusCode >= 500) {
      return new ApiError(i18n.t('errors.server'), ErrorCategory.SERVER, statusCode, error);
    }

    // Other HTTP errors
    return new ApiError(
      error.response?.data?.message || error.message || i18n.t('errors.occurred'),
      ErrorCategory.UNKNOWN,
      statusCode,
      error
    );
  }

  // Handle ApiError instances (already categorized)
  if (error instanceof ApiError) {
    return error;
  }

  // Handle generic Error instances
  if (error instanceof Error) {
    return new ApiError(error.message, ErrorCategory.UNKNOWN, undefined, error);
  }

  // Handle unknown error types
  return new ApiError(i18n.t('errors.unexpected'), ErrorCategory.UNKNOWN, undefined, error);
}

/**
 * Get user-friendly error message based on error category
 *
 * @param error - The ApiError to get message for
 * @returns User-friendly error message
 *
 * @example
 * ```ts
 * const apiError = categorizeError(error);
 * const message = getUserFriendlyMessage(apiError);
 * toast({ variant: 'destructive', description: message });
 * ```
 */
export function getUserFriendlyMessage(error: ApiError): string {
  // If error already has a specific message, use it
  if (error.message && error.message !== i18n.t('errors.unexpected')) {
    return error.message;
  }

  // Provide category-specific fallback messages
  switch (error.category) {
    case ErrorCategory.NETWORK:
      return i18n.t('errors.unableToConnect');

    case ErrorCategory.AUTH:
      return error.statusCode === 401
        ? i18n.t('errors.sessionExpired')
        : i18n.t('errors.noPermission');

    case ErrorCategory.VALIDATION:
      return i18n.t('errors.checkInput');

    case ErrorCategory.SERVER:
      return i18n.t('errors.serversIssues');

    case ErrorCategory.UNKNOWN:
    default:
      return i18n.t('errors.generic');
  }
}

/**
 * Check if an error should be retried automatically
 *
 * @param error - The ApiError to check
 * @returns True if the error is retryable
 *
 * @example
 * ```ts
 * const apiError = categorizeError(error);
 * if (isRetryableError(apiError)) {
 *   // Implement retry logic
 * }
 * ```
 */
export function isRetryableError(error: ApiError): boolean {
  // Network errors and server errors are usually retryable
  if (error.category === ErrorCategory.NETWORK || error.category === ErrorCategory.SERVER) {
    return true;
  }

  // Specific status codes that are retryable
  if (error.statusCode === 408 || error.statusCode === 429 || error.statusCode === 503) {
    return true;
  }

  return false;
}
